import React, { useState, useEffect } from 'react'
import {
  Send,
  X,
  Camera,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Loader2,
  Wallet,
  Lock,
  Shield
} from 'lucide-react'
import { useBalance } from '../contexts/BalanceContext'
import QRScanner from './QRScanner'
import Toast from './Toast'
import api from '../services/api'

function QuickPayment({ isOpen, onClose, preSelectedCategory }) {
  console.log('🔧 QuickPayment component loaded - UPDATED VERSION')

  const {
    categories,
    getCategoryById,
    validatePayment,
    updateCategoryBalance,
    addTransaction,
    loadData
  } = useBalance()

  // Form state
  const [formData, setFormData] = useState({
    amount: '',
    categoryId: '',
    upiId: '',
    merchantName: ''
  })

  // UI state
  const [activeTab, setActiveTab] = useState('upi') // 'upi' or 'scan'
  const [showScanner, setShowScanner] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showSummary, setShowSummary] = useState(false)
  const [paymentSummary, setPaymentSummary] = useState({
    previousBalance: 0,
    amountSpent: 0,
    newBalance: 0,
    categoryName: '',
    categoryColor: ''
  })
  const [isProcessing, setIsProcessing] = useState(false)
  const [showPaymentApps, setShowPaymentApps] = useState(false)
  const [selectedPaymentApp, setSelectedPaymentApp] = useState('')
  const [toast, setToast] = useState(null)
  const [errors, setErrors] = useState({})

  // Payment apps configuration
  const paymentApps = [
    {
      id: 'phonepe',
      name: 'PhonePe',
      icon: '📱',
      color: 'from-purple-500 to-purple-600',
      scheme: 'phonepe://'
    },
    {
      id: 'googlepay',
      name: 'Google Pay',
      icon: '💳',
      color: 'from-blue-500 to-blue-600',
      scheme: 'tez://'
    },
    {
      id: 'paytm',
      name: 'Paytm',
      icon: '💰',
      color: 'from-indigo-500 to-indigo-600',
      scheme: 'paytmmp://'
    },
    {
      id: 'generic',
      name: 'Other UPI Apps',
      icon: '🏦',
      color: 'from-gray-500 to-gray-600',
      scheme: 'upi://'
    }
  ]

  // Default categories for fallback
  const defaultCategories = [
    { id: 'default-food', name: 'Food', balance: 5000, color: '#EF4444' },
    { id: 'default-rent', name: 'Rent', balance: 15000, color: '#3B82F6' },
    { id: 'default-travel', name: 'Travel', balance: 3000, color: '#8B5CF6' },
    { id: 'default-shopping', name: 'Shopping', balance: 7000, color: '#F59E0B' }
  ]

  // Get available categories (user categories + defaults if none exist)
  const availableCategories = categories?.length > 0 ? categories : defaultCategories

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      const initialCategory = preSelectedCategory || availableCategories[0]
      setFormData({
        amount: '',
        categoryId: initialCategory?.id || '',
        upiId: preSelectedCategory?.savedUpiId || '',
        merchantName: ''
      })
      setErrors({})
      setActiveTab('upi')
      setShowConfirmation(false)
      setShowPaymentApps(false)
      setSelectedPaymentApp('')
      setIsProcessing(false)
      setToast(null)

      // Clear any pending payment data
      sessionStorage.removeItem('pendingPayment')

      console.log('🔄 Modal opened - state reset')
    } else {
      // Reset all state when modal closes
      setShowConfirmation(false)
      setShowPaymentApps(false)
      setSelectedPaymentApp('')
      setIsProcessing(false)
      setToast(null)
      sessionStorage.removeItem('pendingPayment')

      console.log('🔄 Modal closed - state reset')
    }
  }, [isOpen, availableCategories, preSelectedCategory])

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen || showScanner) {
      // Save current scroll position
      const scrollY = window.scrollY

      // Lock scroll
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'

      return () => {
        // Restore scroll
        document.body.style.position = ''
        document.body.style.top = ''
        document.body.style.width = ''
        window.scrollTo(0, scrollY)
      }
    }
  }, [isOpen, showScanner])

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  const validateForm = () => {
    const newErrors = {}

    // Validate amount
    const amount = parseFloat(formData.amount)
    if (!formData.amount || isNaN(amount) || amount <= 0) {
      newErrors.amount = 'Enter a valid amount'
    }

    // Validate category
    if (!formData.categoryId) {
      newErrors.categoryId = 'Select a category'
    }

    // Validate UPI ID
    if (!formData.upiId) {
      newErrors.upiId = 'Enter UPI ID'
    } else if (!/^[\w.-]+@[\w.-]+$/.test(formData.upiId)) {
      newErrors.upiId = 'Invalid UPI ID format'
    }

    // Validate balance
    if (formData.categoryId && formData.amount) {
      const category = getCategoryById(formData.categoryId) ||
        defaultCategories.find(c => c.id === formData.categoryId)

      if (category) {
        const balance = category.current_balance || category.balance || 0
        if (amount > balance) {
          newErrors.amount = `Insufficient balance. Available: ₹${balance.toLocaleString('en-IN')}`
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))

    // Clear specific error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleQRScan = (qrData) => {
    try {
      if (qrData?.upiId) {
        setFormData(prev => ({
          ...prev,
          upiId: qrData.upiId,
          merchantName: qrData.merchantName || ''
        }))
        showToast('QR code scanned successfully!')
      }
    } catch (error) {
      console.error('❌ QR scan error:', error)
      showToast('Failed to process QR code', 'error')
    }
    setShowScanner(false)
  }

  const generateUPIUrl = (appScheme = 'upi://') => {
    const { amount, upiId, merchantName, categoryId } = formData
    const category = getCategoryById(categoryId) ||
      defaultCategories.find(c => c.id === categoryId)

    const params = new URLSearchParams({
      pa: upiId,
      pn: merchantName || 'Merchant',
      am: amount,
      cu: 'INR',
      tn: `Payment from ${category?.name || 'Spendly'}`
    })

    return `${appScheme}pay?${params.toString()}`
  }

  const handlePayment = async () => {
    if (!validateForm()) return

    // Show payment app selection
    setShowPaymentApps(true)
  }

  const handlePaymentAppSelection = async (app) => {
    try {
      setIsProcessing(true)
      setSelectedPaymentApp(app.id)

      // Generate UPI URL with selected app scheme
      const upiUrl = generateUPIUrl(app.scheme)
      console.log(`🚀 Redirecting to ${app.name}:`, upiUrl)

      // Store payment data for confirmation (with timestamp)
      const paymentData = {
        ...formData,
        paymentApp: app.name,
        timestamp: Date.now()
      }
      sessionStorage.setItem('pendingPayment', JSON.stringify(paymentData))
      console.log('💾 Stored pending payment data:', paymentData)

      // Hide app selection and show confirmation
      setShowPaymentApps(false)
      setIsProcessing(false)
      setShowConfirmation(true)

      // Try to redirect to selected UPI app
      try {
        window.location.href = upiUrl
      } catch (redirectError) {
        console.log(`${app.name} redirect failed, continuing with manual confirmation`)
      }

    } catch (error) {
      console.error('❌ Payment error:', error)
      showToast('Failed to initiate payment', 'error')
      setIsProcessing(false)
      setShowPaymentApps(false)
    }
  }

  const handlePaymentConfirmation = async (success) => {
    console.log('🔄 Payment confirmation:', success ? 'SUCCESS' : 'FAILED')

    try {
      setIsProcessing(true)

      if (success) {
        const amount = parseFloat(formData.amount)
        const category = getCategoryById(formData.categoryId) ||
          defaultCategories.find(c => c.id === formData.categoryId)

        // Capture previous balance before transaction
        const previousBalance = category?.current_balance || category?.available || 0

        // Create transaction record with pending status first
        const categoryIdStr = String(formData.categoryId)
        const transactionData = {
          category_id: categoryIdStr.startsWith('default-') ? null : formData.categoryId,
          amount,
          merchant_name: formData.merchantName || 'UPI Payment',
          merchant_upi: formData.upiId,
          note: `Payment via ${formData.upiId}`
        }

        // Save to backend if real category
        if (!categoryIdStr.startsWith('default-')) {
          try {
            console.log('💾 Creating transaction in backend...')

            // Step 1: Create transaction (status: pending)
            const createResponse = await api.post('/transactions', transactionData)
            console.log('✅ Transaction created:', createResponse.data)

            const transactionId = createResponse.data.transaction?.id
            if (!transactionId) {
              throw new Error('No transaction ID returned')
            }

            // Step 2: Update status to success (this deducts balance)
            console.log('💾 Updating transaction status to success...')
            await api.put(`/transactions/${transactionId}/status`, { status: 'success' })
            console.log('✅ Transaction status updated to success')

            // Step 3: Refresh data to get updated balance
            await loadData()

            // Calculate new balance directly for immediate display
            // (State update from loadData might be slightly delayed)
            const newBalance = previousBalance - amount

            // Set payment summary data
            setPaymentSummary({
              previousBalance,
              amountSpent: amount,
              newBalance,
              categoryName: category?.name || 'Unknown',
              categoryColor: category?.color || '#6366f1'
            })

            showToast(`Payment of ₹${amount.toLocaleString('en-IN')} completed successfully!`)
            console.log('✅ Payment process completed successfully')

          } catch (apiError) {
            console.error('❌ API Error:', apiError)
            const errorMsg = apiError.response?.data?.message || apiError.userMessage || 'Failed to process payment'
            showToast(errorMsg, 'error')
            return // Don't close modal on error
          }
        } else {
          // For default categories, just add to local state
          const newBalance = previousBalance - amount

          addTransaction({
            id: `TXN${Date.now()}`,
            ...transactionData,
            category_name: category?.name || 'Unknown',
            status: 'success',
            created_at: new Date().toISOString()
          })

          // Set payment summary data
          setPaymentSummary({
            previousBalance,
            amountSpent: amount,
            newBalance,
            categoryName: category?.name || 'Unknown',
            categoryColor: category?.color || '#6366f1'
          })

          showToast(`Payment of ₹${amount.toLocaleString('en-IN')} completed successfully!`)
          console.log('✅ Transaction added to local state')
        }

        // Show summary modal instead of closing
        setShowConfirmation(false)
        setShowSummary(true)

      } else {
        showToast('Payment was cancelled or failed', 'info')

        // Close confirmation and main modal
        setShowConfirmation(false)
        setTimeout(() => onClose(), 500)
      }

      // Clear pending payment
      sessionStorage.removeItem('pendingPayment')

    } catch (error) {
      console.error('❌ Confirmation error:', error)
      showToast('Failed to process payment confirmation', 'error')
    } finally {
      setIsProcessing(false)
    }
  }

  // Check for pending payment on component mount
  useEffect(() => {
    if (!isOpen) return

    const pendingPayment = sessionStorage.getItem('pendingPayment')
    if (pendingPayment) {
      try {
        const paymentData = JSON.parse(pendingPayment)
        console.log('🔍 Found pending payment:', paymentData)

        // Check if payment is recent (within 5 minutes) and matches current form
        const isRecent = Date.now() - paymentData.timestamp < 5 * 60 * 1000
        const matchesCurrentPayment =
          paymentData.amount === formData.amount &&
          paymentData.categoryId === formData.categoryId &&
          paymentData.upiId === formData.upiId

        if (isRecent && matchesCurrentPayment) {
          console.log('✅ Restoring pending payment confirmation')
          setFormData(prev => ({
            ...prev,
            paymentApp: paymentData.paymentApp || ''
          }))
          setShowConfirmation(true)
        } else {
          console.log('🗑️ Clearing old/mismatched pending payment')
          sessionStorage.removeItem('pendingPayment')
        }
      } catch (error) {
        console.error('❌ Pending payment error:', error)
        sessionStorage.removeItem('pendingPayment')
      }
    }
  }, [isOpen, formData.amount, formData.categoryId, formData.upiId])

  const selectedCategory = getCategoryById(formData.categoryId) ||
    defaultCategories.find(c => c.id === formData.categoryId)
  const categoryBalance = selectedCategory?.current_balance || selectedCategory?.balance || 0

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 z-[100] bg-black bg-opacity-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <Send className="h-5 w-5 text-primary-500" />
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
                Quick Payment
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Payment App Selection Modal - Gateway Style */}
          {showPaymentApps && (
            <div className="flex flex-col h-full">
              {/* Gateway Header */}
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center rounded-t-2xl">
                <div className="flex items-center space-x-2">
                  <div className="bg-primary-600 text-white p-1.5 rounded-lg">
                    <Shield className="h-4 w-4" />
                  </div>
                  <div>
                    <h4 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100">Spendly Secure Pay</h4>
                    <p className="text-[10px] sm:text-xs text-green-600 dark:text-green-400 flex items-center">
                      <Lock className="h-3 w-3 mr-1" />
                      100% Safe & Secure
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[10px] sm:text-xs text-gray-500">Order Total</p>
                  <p className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100">₹{parseFloat(formData.amount).toLocaleString('en-IN')}</p>
                </div>
              </div>

              <div className="p-4 sm:p-6">
                <div className="text-center mb-4 sm:mb-6">
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
                    Select Payment Method
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                    Complete your payment using any UPI app
                  </p>
                </div>

                {/* Order Summary Card */}
                <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                  <div className="space-y-2 text-xs sm:text-sm">
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-gray-600 dark:text-gray-400 whitespace-nowrap">Paying to</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate text-right">{formData.merchantName || formData.upiId}</span>
                    </div>
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-gray-600 dark:text-gray-400 whitespace-nowrap">Category</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate text-right">{selectedCategory?.name}</span>
                    </div>
                    <div className="h-px bg-blue-200 dark:bg-blue-800 my-2"></div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-gray-900 dark:text-gray-100">Total Payable</span>
                      <span className="text-base sm:text-lg font-bold text-primary-600 dark:text-primary-400">₹{parseFloat(formData.amount).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>

                {/* Payment App Options - Grid Layout */}
                <div className="space-y-3 mb-6">
                  <p className="text-[10px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3">Recommended Apps</p>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    {paymentApps.map((app) => (
                      <button
                        key={app.id}
                        onClick={() => handlePaymentAppSelection(app)}
                        disabled={isProcessing}
                        className={`relative p-2 sm:p-3 rounded-xl border transition-all duration-200 group text-left flex items-center space-x-2 sm:space-x-3 ${isProcessing && selectedPaymentApp === app.id
                          ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                          : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 hover:shadow-md dark:hover:border-primary-700 bg-white dark:bg-gray-800'
                          }`}
                      >
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br ${app.color} flex items-center justify-center text-lg sm:text-xl shadow-sm group-hover:scale-110 transition-transform`}>
                          {app.icon}
                        </div>
                        <div className="min-w-0">
                          <h5 className="font-semibold text-gray-900 dark:text-gray-100 text-xs sm:text-sm truncate">
                            {app.name}
                          </h5>
                          {isProcessing && selectedPaymentApp === app.id && (
                            <span className="text-[10px] sm:text-xs text-primary-600 animate-pulse block">Processing...</span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Footer Security Badge */}
                <div className="flex justify-center items-center space-x-4 mb-4">
                  <div className="flex items-center space-x-1 grayscale opacity-60">
                    <img src="https://upload.wikimedia.org/wikipedia/commons/e/e1/UPI-Logo-vector.svg" alt="UPI" className="h-3 sm:h-4" />
                  </div>
                  <div className="h-3 w-px bg-gray-300"></div>
                  <p className="text-[10px] sm:text-xs text-gray-400">Powered by Spendly Secure Gateway</p>
                </div>

                {/* Back Button */}
                <button
                  onClick={() => setShowPaymentApps(false)}
                  disabled={isProcessing}
                  className="w-full py-2.5 text-xs sm:text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                >
                  Cancel Transaction
                </button>
              </div>
            </div>
          )}

          {/* Confirmation Modal - Receipt Style */}
          {showConfirmation && (
            <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
              <div className="flex-1 p-4 sm:p-6 flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center shadow-sm mb-4 sm:mb-6 relative">
                  <div className="absolute inset-0 rounded-full border-4 border-secondary-100 dark:border-secondary-900/30 animate-pulse"></div>
                  <CheckCircle className="h-8 w-8 sm:h-10 sm:w-10 text-secondary-500" />
                </div>

                <h4 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-1 sm:mb-2">
                  Payment Submitted
                </h4>
                <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm mb-6 sm:mb-8 max-w-xs mx-auto">
                  Please verify if the transaction was successful in your UPI app to update your budget.
                </p>

                {/* Receipt Card */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 w-full mb-6 sm:mb-8 overflow-hidden relative">
                  {/* Decorative jagged edge */}
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-3 sm:p-4 border-b border-gray-100 dark:border-gray-700">
                    <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-widest font-semibold">Transaction Details</p>
                  </div>
                  <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 dark:text-gray-400 text-xs sm:text-sm">Amount Paid</span>
                      <span className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">₹{parseFloat(formData.amount).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs sm:text-sm">
                      <span className="text-gray-500 dark:text-gray-400">To Merchant</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[150px]">{formData.merchantName || formData.upiId}</span>
                    </div>
                    <div className="flex justify-between items-center text-xs sm:text-sm">
                      <span className="text-gray-500 dark:text-gray-400">Payment App</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100 capitalize">{formData.paymentApp}</span>
                    </div>
                  </div>
                </div>

                <div className="w-full space-y-3">
                  <button
                    onClick={(e) => {
                      console.log('✅ YES button clicked!', e)
                      e.stopPropagation()
                      handlePaymentConfirmation(true)
                    }}
                    disabled={isProcessing}
                    className="w-full py-3 sm:py-3.5 px-4 rounded-xl bg-secondary-600 hover:bg-secondary-700 text-white font-semibold text-sm sm:text-base shadow-lg shadow-secondary-200 dark:shadow-none transition-all active:scale-95 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin mr-2" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                        Yes, Payment Successful
                      </>
                    )}
                  </button>

                  <button
                    onClick={(e) => {
                      console.log('❌ NO button clicked!', e)
                      e.stopPropagation()
                      handlePaymentConfirmation(false)
                    }}
                    disabled={isProcessing}
                    className="w-full py-3 sm:py-3.5 px-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium text-sm sm:text-base hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    No, Payment Failed
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Payment Form */}
          {!showConfirmation && !showPaymentApps && (
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Amount & Category */}
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Amount (₹)
                  </label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    className={`input w-full py-2 text-sm ${errors.amount ? 'border-danger-500' : ''}`}
                    placeholder="Enter amount"
                    min="1"
                    step="1"
                  />
                  {errors.amount && (
                    <p className="text-xs text-danger-600 mt-1">{errors.amount}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Budget Category
                  </label>
                  <select
                    value={formData.categoryId}
                    onChange={(e) => handleInputChange('categoryId', e.target.value)}
                    disabled={!!preSelectedCategory}
                    className={`input w-full py-2 text-sm ${errors.categoryId ? 'border-danger-500' : ''} ${preSelectedCategory ? 'bg-gray-100 dark:bg-gray-700 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Select category</option>
                    {availableCategories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name} - ₹{(category.current_balance || category.balance || 0).toLocaleString('en-IN')}
                      </option>
                    ))}
                  </select>
                  {errors.categoryId && (
                    <p className="text-xs text-danger-600 mt-1">{errors.categoryId}</p>
                  )}

                  {preSelectedCategory && (
                    <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">
                      ✓ Category pre-selected from Home page
                    </p>
                  )}

                  {formData.categoryId && (
                    <div className="mt-2 p-2 sm:p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-600 dark:text-gray-400">Available Balance:</span>
                        <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm sm:text-base">
                          ₹{categoryBalance.toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Method Tabs */}
              <div>
                <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1 mb-3 sm:mb-4">
                  <button
                    onClick={() => setActiveTab('upi')}
                    className={`flex-1 py-1.5 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm rounded-md transition-colors ${activeTab === 'upi'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400'
                      }`}
                  >
                    <CreditCard className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1 sm:mr-2" />
                    Enter UPI ID
                  </button>
                  <button
                    onClick={() => setActiveTab('scan')}
                    className={`flex-1 py-1.5 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm rounded-md transition-colors ${activeTab === 'scan'
                      ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400'
                      }`}
                  >
                    <Camera className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1 sm:mr-2" />
                    Scan QR Code
                  </button>
                </div>

                {/* Manual UPI ID Entry */}
                {activeTab === 'upi' && (
                  <div className="space-y-3 sm:space-y-4">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        UPI ID
                      </label>
                      <input
                        type="text"
                        value={formData.upiId}
                        onChange={(e) => handleInputChange('upiId', e.target.value)}
                        className={`input w-full py-2 text-sm ${errors.upiId ? 'border-danger-500' : ''}`}
                        placeholder="merchant@paytm"
                      />
                      {errors.upiId && (
                        <p className="text-xs text-danger-600 mt-1">{errors.upiId}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Merchant Name (Optional)
                      </label>
                      <input
                        type="text"
                        value={formData.merchantName}
                        onChange={(e) => handleInputChange('merchantName', e.target.value)}
                        className="input w-full py-2 text-sm"
                        placeholder="Merchant Name"
                      />
                    </div>
                  </div>
                )}

                {/* QR Code Scanner */}
                {activeTab === 'scan' && (
                  <div className="text-center py-4 sm:py-8">
                    <Camera className="h-10 w-10 sm:h-16 sm:w-16 text-gray-400 mx-auto mb-3 sm:mb-4" />
                    <p className="text-xs sm:text-small text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
                      Scan a UPI QR code to auto-fill payment details
                    </p>
                    <button
                      onClick={() => setShowScanner(true)}
                      className="btn-primary py-2 px-4 text-sm"
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Open Camera
                    </button>

                    {formData.upiId && (
                      <div className="mt-3 p-2 sm:p-3 bg-secondary-50 dark:bg-secondary-900/20 rounded-lg">
                        <p className="text-xs text-secondary-700 dark:text-secondary-300">
                          ✅ Scanned: {formData.upiId}
                        </p>
                        {formData.merchantName && (
                          <p className="text-xs text-secondary-600 dark:text-secondary-400">
                            {formData.merchantName}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Payment Button */}
              <div className="pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handlePayment}
                  disabled={isProcessing || !formData.amount || !formData.categoryId || !formData.upiId}
                  className="btn-primary w-full py-2.5 sm:py-3 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Proceed to Pay
                </button>
              </div>
            </div>
          )}

          {/* QR Scanner Component - Handles its own modal overlay */}
          <QRScanner
            isOpen={showScanner}
            onClose={() => setShowScanner(false)}
            onScan={handleQRScan}
          />

          {/* Payment Summary Modal */}
          {showSummary && (
            <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center p-4">
              <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-8 relative animate-fadeIn">
                {/* Close button */}
                <button
                  onClick={() => {
                    setShowSummary(false)
                    onClose()
                  }}
                  className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>

                {/* Success Icon */}
                <div className="flex justify-center mb-6">
                  <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-12 w-12 text-green-600 dark:text-green-400" />
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-center text-gray-900 dark:text-gray-100 mb-2">
                  Payment Successful!
                </h3>
                <p className="text-center text-gray-500 dark:text-gray-400 mb-8">
                  Your payment has been processed successfully
                </p>

                {/* Balance Summary Card */}
                <div className="bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-gray-700 dark:to-gray-700 rounded-xl p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Category</span>
                    <div className="flex items-center space-x-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: paymentSummary.categoryColor }}
                      />
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        {paymentSummary.categoryName}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Previous Balance */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Previous Balance</span>
                      <span className="font-semibold text-gray-900 dark:text-gray-100">
                        ₹{paymentSummary.previousBalance.toLocaleString('en-IN')}
                      </span>
                    </div>

                    {/* Amount Spent */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-red-600 dark:text-red-400">Amount Spent</span>
                      <span className="font-bold text-red-600 dark:text-red-400">
                        - ₹{paymentSummary.amountSpent.toLocaleString('en-IN')}
                      </span>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-300 dark:border-gray-600 pt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-base font-semibold text-gray-700 dark:text-gray-300">New Balance</span>
                        <span className="text-xl font-bold text-green-600 dark:text-green-400">
                          ₹{paymentSummary.newBalance.toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Close Button */}
                <button
                  onClick={() => {
                    setShowSummary(false)
                    onClose()
                  }}
                  className="w-full py-3.5 px-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-semibold shadow-lg transition-all active:scale-95"
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {/* Toast Notification */}
          {toast && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[70] w-full max-w-sm px-4">
              <Toast
                message={toast.message}
                type={toast.type}
                onClose={() => setToast(null)}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default QuickPayment
